import React, { useEffect } from 'react'
import { Flex, Text } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import { RotateCcw, Wand2 } from 'lucide-react'
import { RootState } from 'constants/interfaces'
import { Button } from 'components/ui'
import { PromptGeneratorConversation } from './PromptGeneratorConversation'
import useLocaleText from 'components/useLocaleText'
import { localeFallback } from 'components/TasksV2/TaskCreation/utils'
import { usePromptGenerator } from 'components/TasksV2/AITaskCreation/PromptGeneratorContext'

/**
 * Prompt-writing helper, rendered as a step of the AI flow (no nested modal) and taking over the whole
 * modal: the message list fills the body, the input lives in the modal footer. The conversation itself
 * lives in `PromptGeneratorProvider` above the step switch, so leaving and re-entering keeps the history.
 *
 * There is no "apply" action on purpose — the assistant hands the finished prompt over with its own
 * copy button inside the message.
 */
const PromptGeneratorStep: React.FC = () => {
  const { activate, deactivate, conversation, isWebSocketReady, hasWebSocketError, clearHistory } = usePromptGenerator()

  const theme = useSelector((state: RootState) => state.general.theme)
  const elementsColor = theme?.elementsColor || '#FF2D78'

  const t_title = useLocaleText('prompt_generator')
  const t_hint = localeFallback(
    useLocaleText('prompt_generator_hint'),
    'prompt_generator_hint',
    'Describe what you want to check — the assistant will turn it into a ready-to-use prompt.'
  )
  const t_start_over = localeFallback(useLocaleText('start_over'), 'start_over', 'Start over')

  const isEmpty = conversation.length === 0

  // Connect on enter, disconnect on leave — the provider keeps the messages either way.
  useEffect(() => {
    activate()
    return () => deactivate()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex
      flexDir="column"
      flex="1"
      minH={0}
      w="100%"
      bg="white"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
    >
      <Flex alignItems="center" justifyContent="space-between" px="15px" py="10px" flexShrink={0}>
        <Flex alignItems="center" style={{ gap: '6px' }}>
          <Wand2 size={14} color={elementsColor} />
          <Text fontSize="13px" fontWeight="600" color={elementsColor}>
            {t_title}
          </Text>
        </Flex>
        {!isEmpty && (
          <Button variant="ghost" size="sm" startIcon={<RotateCcw size={13} />} onClick={clearHistory}>
            {t_start_over}
          </Button>
        )}
      </Flex>

      {isEmpty && isWebSocketReady && !hasWebSocketError && (
        <Text fontSize="13px" color="#718096" textAlign="center" px="30px" pt="10px" flexShrink={0}>
          {t_hint}
        </Text>
      )}

      <PromptGeneratorConversation />
    </Flex>
  )
}

export default PromptGeneratorStep
